import React, { Fragment, useState, useRef } from 'react';
import {
  Form, ProgressBar, Icon, P, BlankLink
 } from '@contently/toolkit';
import _ from 'underscore';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { DirectUploadProvider } from 'react-activestorage-provider';
import isValid, { VALID_FILE_TYPES } from '../../utils/fileUploadValidator';

const UploadedFile = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: start;
`;

function UploadProgressBar({ uploads }) {
  // currently not supporting multiple uploads
  const upload = uploads[0];

  while (upload.state === 'uploading') {
    const { progress } = upload;
    return <ProgressBar percent={progress} status="active" />;
  }

  return null;
}

export default function DirectAttachmentUpload(props) {
  const { id, onUploadBegin, onUploadSuccess, onUploadError, onRemoveFile, file } = props;
  const [uploadError, setUploadError] = useState(false);
  const inputEl = useRef(null);

  function handleAttachment(signedId) {
    onUploadSuccess(signedId[0]);
  }

  function handleUploadError(msg) {
    // Clear current input value so reuploading a file will trigger change
    inputEl.current.value = '';
    setUploadError(true);
    onUploadError(msg);
  }

  function handleRemoveFile() {
    // Clear current input value so reuploading a file will trigger change
    inputEl.current.value = '';
    setUploadError(false);
    onRemoveFile();
  }

  const UploadInput = ({ handleUpload, uploads }) => {
    const input = (
      <Form.Input
        id={id}
        name={id}
        type="file"
        accept={VALID_FILE_TYPES.join(',')}
        ref={inputEl}
        onChange={e => {
          const tmpFile = e.currentTarget.files[0];
          const valid = isValid(tmpFile, handleUploadError, handleUploadError);
          if (!valid) return;
          onUploadBegin(tmpFile);
          handleUpload([tmpFile]);
        }}
      />
    );

    const progressBar = uploads.length ? <UploadProgressBar uploads={uploads} /> : null;

    return (
      <Fragment>
        <div className="input-uploader">
          <Form.Button variant="secondary" size="small">
            Upload
          </Form.Button>
          {input}
        </div>
        {progressBar}
        {file && !_.isEmpty(file.name) && (
          <UploadedFile>
            <P>
              {file.url ? (
                <BlankLink title="Uploaded file" href={file.url}>
                  {file.name}
                </BlankLink>
              ) : (
                <span>{file.name}</span>
              )}
              {uploadError}
            </P>
            <Icon type="x" onClick={handleRemoveFile} withTextLeft />
          </UploadedFile>
        )}
      </Fragment>
    );
  };

  return <DirectUploadProvider onSuccess={handleAttachment} render={UploadInput} />;
}

export function useDirectAttachmentUploadFormik({ setFieldValue, setErrors, values }) {
  const [isUploading, setIsUploading] = useState(false);

  const onUploadBegin = file => {
    setIsUploading(true);
    setFieldValue('file', { name: file.name });
  };

  const onUploadSuccess = signedId => {
    setIsUploading(false);
    setFieldValue('file', Object.assign(values.file, { signedId }));
  };

  const onRemoveFile = () => {
    setFieldValue('file', null);
  };

  const onUploadError = error => setErrors({ file: error });

  return [
    isUploading,
    {
      onUploadBegin,
      onUploadSuccess,
      onRemoveFile,
      onUploadError,
      file: values.file
    }
  ];
}

DirectAttachmentUpload.propTypes = {
  onUploadBegin: PropTypes.func.isRequired,
  onUploadSuccess: PropTypes.func.isRequired,
  onUploadError: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
  file: PropTypes.shape({
    name: PropTypes.string,
    url: PropTypes.string
  })
};

DirectAttachmentUpload.defaultProps = {
  file: { name: '', url: '' }
};



// WEBPACK FOOTER //
// ./app/javascript/app/components/forms/active_storage_direct_uploader.jsx